import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildMockOverviewData } from '../services/mockOverviewService';
import { toOverviewLocation } from '../services/overviewMetricsService';
import { getApiErrorMessage } from '../utils/errorMessageUtils';
import type { OverviewData, OverviewLocation } from '../types/overviewTypes';

interface UseOverviewTargetsResult {
  data: OverviewData | null;
  isLoading: boolean;
  loadError: string | null;
  // Runs the load again. The page stays mounted behind an inline error, so
  // without this the only way back from a failure is to navigate away and
  // return.
  refresh: () => void;
}

/**
 * Target and progress figures for every accessible location, for one year.
 *
 * Currently served by `mockOverviewService`. The call is async and the loading
 * and error states are real, so swapping the mock for the REH-213 endpoint is a
 * change to one line in this hook and nothing else.
 *
 * Whatever answers, its rows go through `toOverviewLocation` before anything
 * reads them: that is the seam where an endpoint's optional numbers become the
 * figures the cards can trust.
 */
export function useOverviewTargets(
  locationIds: string[],
  year: number
): UseOverviewTargetsResult {
  const { t } = useTranslation();
  const [data, setData] = useState<OverviewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Joined rather than passed as an array: a fresh array identity on every
  // render of the caller would restart the fetch forever.
  const locationKey = locationIds.join(',');

  // Bumped by `refresh`, which is the whole mechanism: the effect below already
  // holds the load, and re-running it is cheaper to reason about than a second
  // copy of the same fetch that a retry button could call directly.
  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  // Held in a ref so `t` can stay out of the effect's deps: it changes identity
  // on a language switch, and refiring the whole load for a fallback string
  // would blank the figures already on screen to re-render one message.
  const fallbackMessageRef = useRef(t('overview-load-error'));
  useEffect(() => {
    fallbackMessageRef.current = t('overview-load-error');
  }, [t]);

  useEffect(() => {
    let isStale = false;

    const load = async (): Promise<void> => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const ids = locationKey === '' ? [] : locationKey.split(',');
        const response = await Promise.resolve(buildMockOverviewData(ids, year));
        if (isStale) return;
        // Rows without a location id are dropped rather than shown: figures with
        // nothing to attach them to would have to be guessed onto a card.
        setData({
          year: response.year,
          locations: response.locations
            .map((location) => toOverviewLocation(location))
            .filter((location): location is OverviewLocation => location !== null)
        });
      } catch (e) {
        // Logged before the staleness check, and so unconditionally: there is no
        // telemetry, the console is the only breadcrumb, and a response nobody
        // wants any more is still the one that shows the endpoint is down.
        console.error('Failed to load the overview figures:', e);
        if (isStale) return;
        // Drop the previous response as well as reporting the failure. The card
        // header renders the selected year, so keeping the last successful data
        // would put the previous year's money under the new year's heading.
        setData(null);
        // getApiErrorMessage, not getErrorMessage: this string is shown to the
        // planner, and an AxiosError's own `.message` reads "Request failed with
        // status code 500". A server-sent message is preferred where there is
        // one, and the localized fallback used otherwise.
        setLoadError(getApiErrorMessage(e, fallbackMessageRef.current));
      } finally {
        if (!isStale) setIsLoading(false);
      }
    };

    void load();
    return () => {
      isStale = true;
    };
  }, [locationKey, year, reloadToken]);

  return { data, isLoading, loadError, refresh };
}
